import dayjs from 'utils/dayjs-duration';

import isNil from 'lodash/isNil';

import {
    Page,
    fetchCity,
    fetchDestination,
    foldWithSearchState,
    isMonthlyPage,
} from 'utils/page-utils';
import CMSUtils from 'utils/cms';
import EventsAPI from 'api/events';
import SearchUtils from 'utils/search-utils';
import {interpolatePrismicPage} from 'cms/utils/link-utils';
import {PRISMIC_CUSTOM_TYPE} from 'utils/prismic-types';
import DynamicRatesAPI from 'api/dynamic-rates';
import {initialState as searchRequestInitialState} from 'store/search-request/search-request-reducer';
import AppRatingAPI from 'api/app-rating';
import ErrorUtils from 'utils/error-utils';
import {getTransientFacilities} from 'api/search-v2/search-api';
import {destinationLimits} from 'utils/search-limits';

const PRISMIC_SLUG_COOKIE_CUTTER_ALL = 'cookie-cutter-all';
const PRISMIC_SLUG_COOKIE_CUTTER_EVENTS = 'cookie-cutter-events';

/**
 * ### isEventTabActive
 * A function that is used to determine if the Events Tab on the Destination
 * Midfunnel pages should be shown on load. Determines if the Event tab is active
 * based off of how many events are present and how far out (in days) the first
 * event is. If there are at least 5 events, and the first event is within 7 days
 * from the current time, this returns `true`. This can also take in an override
 * value that an author can set within the Prismic page.
 *
 * @param {{events: Array, defaultTabOverride: string, now: object}} args - The function arguments.
 * @param {Array} args.events - An array of Events.
 * @param {string} args.defaultTabOverride - An override to the default behavior. Can be either Event or Hourly.
 * @param {object} args.now - The current time object (dayjs).
 * @returns {boolean} - Is the Event Tab active or not.
 */
export const isEventTabActive = ({
    events = [],
    defaultTabOverride,
    now = dayjs,
}) => {
    const MIN_EVENT_COUNT = 5;
    const MIN_EVENT_START_DAYS = 7;
    const eventCount = events.length;
    const isEventStartingSoon = events.some(
        event => dayjs(event.starts).diff(now(), 'days') < MIN_EVENT_START_DAYS
    );

    // Prismic override: allow marketing team to override tab selection logic
    if (Boolean(defaultTabOverride) && defaultTabOverride !== 'Event')
        return false;

    return eventCount >= MIN_EVENT_COUNT || isEventStartingSoon;
};

/**
 * ### isPrismicDestinationPage
 * A function that determines whether or not the page should be a Prismic page or
 * not. If there are query parameters this should return false. Pages that have
 * either bespoke Prismic content or the cookie cutter flag should return true.
 *
 * @param {{bespokeDestinationPage: object, isCookieCutterContent: boolean, query: object}} args - The function arguments.
 * @param {object} args.bespokeDestinationPage - The Prismic page response containing all the content for the Midfunnel page.
 * @param {boolean} args.isCookieCutterContent - A boolean retrived from admin to determine if a Destination page without custom content should use the cookie cutter content/template.
 * @param {object} args.query - The query object.
 * @returns {boolean} - Is the page a Midfunnel Destination page or not.
 */
export const isPrismicDestinationPage = ({
    bespokeDestinationPage,
    isCookieCutterContent,
    query,
}) => {
    const hasQueryParams = Boolean(Object.keys(query).length);

    return (bespokeDestinationPage || isCookieCutterContent) && !hasQueryParams;
};

/**
 * ### fetchBespokeDestinationPageFromPrismic
 * A function that returns the Prismic Page object containing all the content for
 * a page. If there is not a Prismic page present, the function should return `null`.
 *
 * @param {{cookies: object, prismicSlug: string}} args - The function arguments
 * @param {object} args.cookies - The object containing the active cookies. For the purposes of the Destination page, the Prismic preview cookie is the only relevant one.
 * @param {string} args.prismicSlug - The slug (UID) for the Prismic page being fetched.
 * @returns {object|null} - The Prismic document object or a null value if a Prismic page does not exist.
 */
export const fetchBespokeDestinationPageFromPrismic = async ({
    cookies,
    prismicSlug,
}) => {
    try {
        return await CMSUtils.getPageData({
            cookies,
            slug: prismicSlug,
            customType: PRISMIC_CUSTOM_TYPE.DESTINATION_MIDFUNNEL,
            pageType: Page.MID_FUNNEL,
        });
    } catch (e) {
        return null;
    }
};

/**
 * ### parseParams
 * A function that takes in the route parameters and returns the parsed
 * `destinationSlug` and `citySlug`.
 *
 * @param {{city: string, slug: string}} params - The object contianing the route parameters.
 * @returns {{destinationSlug: string, citySlug: string}} - An object containing the `destinationSlug` (with the potential `-parking` string removed) and `citySlug`.
 */
export const parseParams = params => {
    const destinationSlug = params.slug.replace(/-parking$/i, '');
    const {city: citySlug} = params;

    return {
        destinationSlug,
        citySlug,
    };
};

const getInitialState = async ({state, params, query, cookies, route}) => {
    /* --------------------------------------------------------------------------- *\
        Params is made up of the url parameters, in this case:
            params: {
                city: citySlug,
                slug: destinationSlug,
            }
        Because the slug may or may not have `-parking` appended to itself, the
        destinationSlug needs to be treated separately.
    \* --------------------------------------------------------------------------- */
    // make shared api calls; city, destination, page, and event
    const {destinationSlug, citySlug} = parseParams(params);

    const [city, destination] = await Promise.all([
        fetchCity(state, citySlug),
        fetchDestination({slug: destinationSlug}, state, citySlug),
    ]);

    const isMonthly = isMonthlyPage(route);
    const {
        data: {timezone},
    } = city;

    const {
        data: {
            categories,
            latitude,
            longitude,
            id: destinationId,
            title,
            has_upcoming_events: hasEvents,
            can_have_events: canHaveEvents,
            is_prismic_cookie_cutter: isCookieCutterContent,
        },
    } = destination;

    const prismicSlug = `${destinationId}`;
    const bespokeDestinationPage = await fetchBespokeDestinationPageFromPrismic(
        {cookies, prismicSlug}
    );

    const isAirportPage = categories.some(category => category === 4);
    const isPrismicPage = isPrismicDestinationPage({
        bespokeDestinationPage,
        isCookieCutterContent,
        query,
    });

    if (isMonthly) {
        query.monthly = isMonthly;

        return foldWithSearchState({
            state: {
                ...state,
                city,
                destination,
            },
            type: Page.DESTINATION,
            latitude,
            longitude,
            timezone,
            query,
            cookies,
        });
    }

    // Returns the state for Prismic Pages (both bespoke and cookie-cutter content)
    const featuredSpotCount = 6;

    try {
        if (isPrismicPage && !isAirportPage) {
            const [
                cookieCutterPageForAll,
                cookieCutterPageForEvent,
                dynamicRates,
                appRating,
                featuredSpots,
            ] = await Promise.all([
                CMSUtils.getPageData({
                    cookies,
                    slug: PRISMIC_SLUG_COOKIE_CUTTER_ALL,
                    customType: PRISMIC_CUSTOM_TYPE.DESTINATION_MIDFUNNEL,
                    pageType: Page.MID_FUNNEL,
                }),
                CMSUtils.getPageData({
                    cookies,
                    slug: PRISMIC_SLUG_COOKIE_CUTTER_EVENTS,
                    customType: PRISMIC_CUSTOM_TYPE.DESTINATION_MIDFUNNEL,
                    pageType: Page.MID_FUNNEL,
                }),
                DynamicRatesAPI.getRatesByDestinationID(destinationId),
                AppRatingAPI.get(),
                getTransientFacilities(
                    {
                        // eslint-disable-next-line camelcase
                        page_size: featuredSpotCount,
                        // eslint-disable-next-line camelcase
                        max_distance_meters: destinationLimits.maxDistance,
                        lat: latitude,
                        lon: longitude,
                    },
                    {}
                ),
            ]);

            const page =
                bespokeDestinationPage ||
                (canHaveEvents
                    ? cookieCutterPageForEvent
                    : cookieCutterPageForAll);

            const interpolations = {
                '{Destination-Title}': title,
            };
            const interpolatedPage = await interpolatePrismicPage(
                page,
                interpolations
            );

            const searchRequest = {
                ...searchRequestInitialState,
                pageType: Page.MID_FUNNEL,
                ...SearchUtils.createTimeWindowParams(
                    Page.MID_FUNNEL,
                    {},
                    timezone
                ),
            };

            if (canHaveEvents) {
                destination.data.events = await EventsAPI.get({
                    destinationId,
                }).then(res => res.data.data.results);

                searchRequest.event = isEventTabActive({
                    events: destination.data.events,
                    defaultTabOverride: page.default_tab,
                });
            }

            return {
                ...state,
                city,
                destination,
                searchRequest,
                page: interpolatedPage,
                cms: {
                    interpolations,
                },
                dynamicRates: {
                    data: dynamicRates,
                },
                appRating: {...appRating, isPending: false},
                midfunnel: {
                    featuredSpots: {
                        data: featuredSpots.results,
                        isPending: false,
                    },
                },
            };
        }
    } catch (error) {
        ErrorUtils.sendSentryError(error);
    }

    /* ---------------------------------------------------------------- *\
        Returns the state for non-Prismic Pages (old Destination
        Search experience) for a singel event (eid is present within
        the query - `/search?kind=event&id=1` gets translated to
        `/search?eid=1` within Search.getInitialState).
    \* ---------------------------------------------------------------- */
    if (!isNil(query.eid)) {
        const {data: event} = await EventsAPI.getById(query.eid);

        return foldWithSearchState({
            state: {
                ...state,
                city,
                destination,
                event,
            },
            type: Page.EVENT,
            latitude,
            longitude,
            timezone,
            query: {
                ...query,
                starts: event.starts,
                ends: event.ends,
            },
            cookies,
        });
    }

    // Returns the State for non-Prismic Destination ages (old Search experience)
    if (isNil(query.eid)) {
        if (hasEvents) {
            destination.data.events = await EventsAPI.get({destinationId}).then(
                res => res.data.data.results
            );
        }

        return foldWithSearchState({
            state: {
                ...state,
                city,
                destination,
            },
            type: hasEvents ? 'venue' : Page.DESTINATION,
            latitude,
            longitude,
            timezone,
            query,
            cookies,
        });
    }
};

export default getInitialState;
